/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FaHome } from 'react-icons/fa';
import { MODULE_CONFIG, useModule } from '../ModuleContext';

// Catálogos de navegação
import { MODULES_NAV, GROUP_CONFIGURACOES } from './nav';

// Componentes de renderização
import ModuleSelector from './components/ModuleSelector';
import MenuGroup from './components/MenuGroup';
import TopItem from './components/TopItem';

// Hooks
import usePermissions from './hooks/usePermissions';
import useOpenGroups from './hooks/useOpenGroups';
import useActivePopover from './hooks/useActivePopover';

// ---------------------------------------------------------------------------
// Home fixa (2026-08-21).
//
// Item de menu SEM RBAC e independente do módulo ativo: a home (/main) é um hub
// que só reflete o que o usuário já pode ver, então não há o que gatear. Antes
// desta entrada o /main era topItem do módulo `estoque` gateado por
// `inventory:stock:read` — quem não tinha essa chave (financeiro, RH, comercial)
// não tinha NENHUM caminho de volta pra home.
// ---------------------------------------------------------------------------
const HOME_ITEM = {
   to: '/main',
   title: 'Página inicial',
   label: 'Início',
   icon: FaHome,
};

/**
 * Componente principal do menu lateral.
 * Responsabilidades:
 *   - Lê catálogos de navegação (MODULES_NAV, GROUP_CONFIGURACOES)
 *   - Filtra por permissões do usuário
 *   - Coordena estado entre ModuleSelector, MenuGroups e TopItems
 *   - Reseta estado apropriadamente ao colapsar/trocar rota
 *
 * Os estilos vivem em src/components/Menu/styled.js (o <Nav> wrapper do
 * Header). Este componente só renderiza marcação com as classes que o
 * styled.js já conhece — não define estilos próprios.
 */
function MenuItems({ onNavigate = undefined, collapsed = false }) {
   const { hasPermission, hasAnyPermission, isActive, permissions, pathname } =
      usePermissions();
   const { activeModule, selectModule } = useModule();

   // Grupos monitorados pra auto-abrir o da rota ativa
   const allGroups = useMemo(() => {
      const moduleGroups = (activeModule && MODULES_NAV[activeModule]?.groups) || [];
      return [...moduleGroups, GROUP_CONFIGURACOES];
   }, [activeModule]);

   const { openGroups, toggleGroup, resetGroups } = useOpenGroups({
      pathname,
      allGroups,
      collapsed,
   });

   const { activeKey, openPopover, closePopover } = useActivePopover();

   // Módulos disponíveis pro usuário
   const availableModules = useMemo(
      () =>
         Object.entries(MODULE_CONFIG).filter(([, cfg]) =>
            cfg.requiredPermissions.some((p) => permissions.includes(p))
         ),
      [permissions]
   );

   const hasConfigAccess = hasAnyPermission(GROUP_CONFIGURACOES.requiredAny);

   // Reset ao colapsar/expandir
   useEffect(() => {
      resetGroups();
      closePopover();
   }, [collapsed, resetGroups, closePopover]);

   // Reset ao trocar de rota
   useEffect(() => {
      closePopover();
   }, [pathname, closePopover]);

   // -------------------------------------------------------------------------
   // "Início" zera o contexto de navegação da sidebar (2026-08-22).
   //
   // Sem isso, quem fosse em Estoque > Movimentos > Entrada de produtos e
   // voltasse pra home continuava vendo o menu de Estoque aberto — a home
   // "encaminha", mas a sidebar seguia presa ao caminho anterior.
   //
   // Fecha também os grupos expandidos, senão o accordion do módulo antigo
   // sobreviveria à troca.
   // -------------------------------------------------------------------------
   const handleGoHome = useCallback(() => {
      selectModule(null);
      resetGroups();
      closePopover();
      if (onNavigate) onNavigate();
   }, [selectModule, resetGroups, closePopover, onNavigate]);

   // -------------------------------------------------------------------------
   // Renderização de um grupo (delega pro MenuGroup)
   // -------------------------------------------------------------------------
   const renderGroup = (group) => {
      const visibleChildren = group.children.filter((c) =>
         hasAnyPermission(c.permissionAny)
      );
      if (!visibleChildren.length) return null;

      const hasActiveChild = visibleChildren.some((c) => isActive(c.to));

      return (
         <MenuGroup
            key={group.key}
            group={group}
            visibleChildren={visibleChildren}
            collapsed={collapsed}
            isOpen={!!openGroups[group.key]}
            hasActiveChild={hasActiveChild}
            isActive={isActive}
            onToggle={toggleGroup}
            onNavigate={onNavigate}
            popoverOpen={activeKey === group.key}
            onOpenPopover={openPopover}
            onClosePopover={closePopover}
         />
      );
   };

   // -------------------------------------------------------------------------
   // Conteúdo do módulo ativo (topItems + groups)
   // -------------------------------------------------------------------------
   const renderModuleContent = () => {
      if (!activeModule) {
         if (collapsed) return null;
         return (
            <p
               style={{
                  fontSize: '0.75rem',
                  color: '#9ca3af',
                  padding: '8px 4px 0',
                  margin: 0,
               }}
            >
               Selecione um módulo para navegar.
            </p>
         );
      }

      const nav = MODULES_NAV[activeModule];
      if (!nav) return null;

      const visibleTopItems = nav.topItems.filter((item) =>
         hasPermission(item.permission)
      );

      return (
         <>
            {!collapsed && <h3>{MODULE_CONFIG[activeModule]?.label}</h3>}
            <div className="menu-items">
               {visibleTopItems.map((item) => (
                  <TopItem
                     key={item.to}
                     item={item}
                     active={isActive(item.to)}
                     collapsed={collapsed}
                     onNavigate={onNavigate}
                  />
               ))}
               {nav.groups.map(renderGroup)}
            </div>
         </>
      );
   };

   // -------------------------------------------------------------------------
   // Grupo fixo de Configurações
   // -------------------------------------------------------------------------
   const renderConfiguracoes = () => {
      if (!hasConfigAccess) return null;
      return (
         <div className="settings-fixed">
            {!collapsed && <h3>Configurações</h3>}
            {renderGroup(GROUP_CONFIGURACOES)}
         </div>
      );
   };

   return (
      <>
         {/* Voltar pra home LIMPA o módulo ativo (decisão 2026-08-22, reverte
             o comportamento de 2026-08-21): a home é ponto de partida, então a
             sidebar volta ao estado neutro em vez de manter o módulo de onde o
             usuário veio. */}
         <div className="home-fixed">
            <TopItem
               item={HOME_ITEM}
               active={isActive(HOME_ITEM.to)}
               collapsed={collapsed}
               onNavigate={handleGoHome}
            />
         </div>

         <ModuleSelector
            availableModules={availableModules}
            activeModule={activeModule}
            onSelectModule={selectModule}
            collapsed={collapsed}
         />

         <div className="nav-content">{renderModuleContent()}</div>

         {renderConfiguracoes()}
      </>
   );
}

MenuItems.propTypes = {
   onNavigate: PropTypes.func,
   collapsed: PropTypes.bool,
};

export default MenuItems;
